import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ActionButtons from './ActionButtons';

/**
 * Tabla de datos unificada específica para equipaciones
 * Basada en UnifiedDataTable pero adaptada para la estructura de datos de equipaciones
 */
const EquipacionUnifiedDataTable = ({
  columns,
  data,
  actions = [],
  pagination = true,
  searchable = true,
  selectable = false,
  onRowClick = null,
  emptyMessage = 'No hay solicitudes de equipación disponibles',
  loading = false,
  itemsPerPage = 10,
  className = '',
  // Props para paginación del servidor
  serverPagination = false,
  paginationInfo = null,
  onPageChange = null,
  onLimitChange = null
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  // Función para obtener ID único de fila (adaptado para equipaciones)
  const getRowId = (row) => row.transaction_id || row.id || row._id || row.key;

  // Filtrar datos según el término de búsqueda
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;

    return data.filter((item) =>
      columns.some(
        (column) =>
          column.searchable !== false &&
          item[column.key] &&
          item[column.key].toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm, columns, searchable]);

  // Ordenar datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginar datos (solo para paginación del cliente)
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    // Si usamos paginación del servidor, no paginamos en el cliente
    if (serverPagination) return sortedData;

    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage, pagination, serverPagination]);

  // Calcular información de paginación
  const totalPages = serverPagination && paginationInfo
    ? paginationInfo.pages
    : Math.ceil(sortedData.length / itemsPerPage);

  const currentPageNumber = serverPagination && paginationInfo
    ? paginationInfo.page
    : currentPage;

  // Manejar ordenamiento
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Manejar selección de filas
  const handleSelectRow = (rowId) => {
    setSelectedRows(prev =>
      prev.includes(rowId)
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  // Manejar selección de todas las filas
  const handleSelectAll = () => {
    if (selectedRows.length === paginatedData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(paginatedData.map(getRowId));
    }
  };

  // Manejar cambio de página
  const handlePageChangeInternal = (newPage) => {
    if (serverPagination && onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  // Manejar cambio de límite
  const handleLimitChangeInternal = (newLimit) => {
    if (serverPagination && onLimitChange) {
      onLimitChange(newLimit);
    } else {
      // Para paginación del cliente, recalcular página actual
      setCurrentPage(1);
    }
  };

  if (loading) {
    return (
      <div className="unified-table-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Cargando solicitudes de equipación...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`unified-data-table ${className}`}>
      {/* Barra de herramientas */}
      <div className="table-toolbar">
        {/* Búsqueda */}
        {searchable && (
          <div className="search-container">
            <span className="material-icons">search</span>
            <input
              type="text"
              placeholder="Buscar equipaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        )}

        {/* Acciones masivas */}
        {selectable && selectedRows.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedRows.length} elemento(s) seleccionado(s)</span>
            {/* Aquí se pueden agregar acciones masivas */}
          </div>
        )}

        {/* Información de resultados */}
        <div className="results-info">
          {serverPagination && paginationInfo
            ? `${paginationInfo.total} resultado(s) total - Mostrando página ${paginationInfo.page} de ${paginationInfo.pages}`
            : `${sortedData.length} solicitud(es) de equipación`
          }
        </div>
      </div>

      {/* Tabla */}
      <div className="table-responsive">
        <table className="unified-table">
          <thead>
            <tr>
              {selectable && (
                <th className="select-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.sortable !== false ? 'sortable' : ''} ${
                    sortConfig.key === column.key ? `sorted-${sortConfig.direction}` : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="th-content">
                    <span>{column.label}</span>
                    {column.sortable !== false && (
                      <span className="sort-icon material-icons">
                        {sortConfig.key === column.key
                          ? (sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward')
                          : 'unfold_more'
                        }
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && <th className="actions-column">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}
                  className="empty-table"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr
                  key={getRowId(row)}
                  className={`${selectedRows.includes(getRowId(row)) ? 'selected' : ''} ${
                    onRowClick ? 'clickable' : ''
                  }`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {selectable && (
                    <td className="select-column" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(getRowId(row))}
                        onChange={() => handleSelectRow(getRowId(row))}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={`${getRowId(row)}-${column.key}`}>
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="actions-column" onClick={(e) => e.stopPropagation()}>
                      <ActionButtons actions={actions} row={row} />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {pagination && totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Página {currentPageNumber} de {totalPages}
            {serverPagination && paginationInfo && (
              <span className="pagination-details">
                {' '}({paginationInfo.limit} por página)
              </span>
            )}
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChangeInternal(1)}
              disabled={currentPageNumber === 1}
              className="pagination-btn"
            >
              <span className="material-icons">first_page</span>
            </button>
            <button
              onClick={() => handlePageChangeInternal(Math.max(currentPageNumber - 1, 1))}
              disabled={currentPageNumber === 1}
              className="pagination-btn"
            >
              <span className="material-icons">chevron_left</span>
            </button>
            <span className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(currentPageNumber - 2 + i, totalPages - 4 + i));
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChangeInternal(pageNum)}
                    className={`page-btn ${currentPageNumber === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </span>
            <button
              onClick={() => handlePageChangeInternal(Math.min(currentPageNumber + 1, totalPages))}
              disabled={currentPageNumber === totalPages}
              className="pagination-btn"
            >
              <span className="material-icons">chevron_right</span>
            </button>
            <button
              onClick={() => handlePageChangeInternal(totalPages)}
              disabled={currentPageNumber === totalPages}
              className="pagination-btn"
            >
              <span className="material-icons">last_page</span>
            </button>
          </div>

          {/* Selector de límite para paginación del servidor */}
          {serverPagination && onLimitChange && (
            <div className="pagination-limit">
              <label>
                Mostrar:
                <select
                  value={paginationInfo?.limit || 50}
                  onChange={(e) => handleLimitChangeInternal(Number(e.target.value))}
                  className="limit-select"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                por página
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

EquipacionUnifiedDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    render: PropTypes.func,
    sortable: PropTypes.bool,
    searchable: PropTypes.bool
  })).isRequired,
  data: PropTypes.array.isRequired,
  actions: PropTypes.array,
  pagination: PropTypes.bool,
  searchable: PropTypes.bool,
  selectable: PropTypes.bool,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  className: PropTypes.string,
  // Props para paginación del servidor
  serverPagination: PropTypes.bool,
  paginationInfo: PropTypes.shape({
    total: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
    limit: PropTypes.number
  }),
  onPageChange: PropTypes.func,
  onLimitChange: PropTypes.func
};

export default EquipacionUnifiedDataTable;
